// Map browser data: everything known about each map (the server catalog,
// arena info, timeruns, favourites), plus search, filters and
// sorting. Maps come from the server's catalog and from this device (built-in
// and downloaded bundles), so the browser also works offline.

import type { VirtualFS } from '../../fs/vfs';
import type { Settings } from '../../game/settings';
import type { ArenaInfo } from '../../shared/arena';
import type { MapInfo } from '../../shared/net/protocol';
import { stripColors } from '../etColors';
import { peekMapMeta, type MapMeta } from './mapMeta';

export interface MapEntry {
  name: string;
  /** Display name without colour codes (arena longname or worldspawn message). */
  title: string;
  /** Display name with colour codes. */
  longname: string | null;
  author: string | null;
  briefing: string | null;
  /** Undefined until the map was indexed. */
  meta: MapMeta | undefined;
  /** The server's catalog entry (online). */
  info: MapInfo | null;
  /** Playable without downloading. */
  local: boolean;
  favorite: boolean;
  /** Last time played (ms), 0 if never. */
  playedAt: number;
}

export type MapFilter = 'all' | 'favorites' | 'recent' | 'timerun' | 'local';
export type MapSort = 'name' | 'recent' | 'difficulty';

export interface ServerCatalog {
  maps: MapInfo[];
}

export function mapEntries(vfs: VirtualFS, localMaps: string[], arenas: ReadonlyMap<string, ArenaInfo>, s: Settings, server: ServerCatalog | null): MapEntry[] {
  const played = new Map(s.recentMaps.map((r) => [r.name, r.at]));
  const favorites = new Set(s.favorites);
  const serverMaps = new Map((server?.maps ?? []).map((m) => [m.name, m]));
  const local = new Set(localMaps);
  const names = [...new Set([...serverMaps.keys(), ...localMaps])];
  return names.map((name) => {
    const info = serverMaps.get(name) ?? null;
    const arena = arenas.get(name) ?? null;
    const meta: MapMeta | undefined = info ? { runs: info.runs, rules: info.rules, message: info.longname } : local.has(name) ? peekMapMeta(vfs, name) : undefined;
    const longname = info?.longname ?? arena?.longname ?? meta?.message ?? null;
    return {
      name,
      title: longname ? stripColors(longname).trim() || name : name,
      longname,
      author: info?.author ?? arena?.author ?? null,
      briefing: info?.briefing ?? arena?.briefing ?? null,
      meta,
      info,
      local: local.has(name),
      favorite: favorites.has(name),
      playedAt: played.get(name) ?? 0,
    };
  });
}

/**
 * Fuzzy match: every query character in order. Higher scores for matches at
 * the start, at word starts and in runs; null when it does not match.
 */
export function fuzzyScore(query: string, text: string): number | null {
  const q = query.toLowerCase().replace(/\s+/g, '');
  if (!q) return 0;
  const t = text.toLowerCase();
  const direct = t.indexOf(q);
  if (direct >= 0) return 1000 - direct * 2 - (t.length - q.length) * 0.1;
  let score = 0;
  let ti = 0;
  let prev = -2;
  for (const c of q) {
    const found = t.indexOf(c, ti);
    if (found < 0) return null;
    const wordStart = found === 0 || /[\s_\-.]/.test(t[found - 1]);
    score += found === prev + 1 ? 8 : wordStart ? 6 : 1;
    prev = found;
    ti = found + 1;
  }
  return score - t.length * 0.05;
}

/**
 * @param tag only maps the server tagged with it
 * @param difficulty only maps of this difficulty (1..5)
 */
export function filterMaps(entries: MapEntry[], opts: { query: string; filter: MapFilter; sort: MapSort; tag?: string | null; difficulty?: number | null }): MapEntry[] {
  const scored: { e: MapEntry; score: number }[] = [];
  for (const e of entries) {
    if (opts.tag && !e.info?.tags?.includes(opts.tag)) continue;
    if (opts.difficulty && e.info?.difficulty !== opts.difficulty) continue;
    if (opts.filter === 'favorites' && !e.favorite) continue;
    if (opts.filter === 'recent' && !e.playedAt) continue;
    if (opts.filter === 'timerun' && !e.meta?.runs.length) continue;
    if (opts.filter === 'local' && !e.local) continue;
    const a = fuzzyScore(opts.query, e.name);
    const b = e.title !== e.name ? fuzzyScore(opts.query, e.title) : null;
    if (a === null && b === null) continue;
    scored.push({ e, score: Math.max(a ?? -Infinity, b ?? -Infinity) });
  }
  scored.sort((x, y) => {
    if (opts.query && x.score !== y.score) return y.score - x.score;
    if (opts.sort === 'recent' && x.e.playedAt !== y.e.playedAt) return y.e.playedAt - x.e.playedAt;
    if (opts.sort === 'difficulty') {
      // easiest first, maps without a difficulty last
      const dx = x.e.info?.difficulty ?? 9;
      const dy = y.e.info?.difficulty ?? 9;
      if (dx !== dy) return dx - dy;
    }
    return x.e.name.localeCompare(y.e.name);
  });
  return scored.map((s) => s.e);
}
